import { Injectable } from '@angular/core';
import { AlcmeonContextService } from '../core/alcmeon-context/alcmeon-context.service';
import { TaskState } from '../shared/models/misc.model';
import {
  AnyTaskOrActionType,
  GenericAction,
  LazyloadTasksOrActionsResponse,
  Task,
  TaskForProcessingResponse,
  TaskOrActionSet,
  TaskOrActionType,
} from './shared/model';
import { MESSAGE_PAGE_SIZE, TaskProcessService } from './shared/task-process.service';

@Injectable({
  providedIn: 'root',
})
export class ChunksAndIds {
  // The chunks are in chronological order too
  // We'll insert new chunks at the start of the array when we lazyload older and older messages,
  // and at the end of the array when we insert new tasks or actions received via websocket while the page is displayed
  loadedChunks: AnyTaskOrActionType[][] = [];
  processedTask: Task;

  // These ids are in chronological order, as returned by the backend
  private notYetLoadedIds: string[] = [];

  // We consider the user sees all chunks when the page loads, and when they click the "scroll down to new messages" button
  private mostRecentChunkSeen: AnyTaskOrActionType[];
  private allUnseenMessagesAreFromLoggedUser = true;

  constructor(private alcmeonContextService: AlcmeonContextService, private taskProcessService: TaskProcessService) {}

  init(taskForProcessingResponse: TaskForProcessingResponse) {
    this.notYetLoadedIds = [...taskForProcessingResponse.tasksOrActionsIds]; // clone the array as it will get sliced
    this.addNewChunkOfOlderMessages(taskForProcessingResponse.tasksOrActionsById);
    this.mostRecentChunkSeen = this.chunkWithMostRecentMessages();
    this.processedTask = taskForProcessingResponse.engagement
      ? null
      : (taskForProcessingResponse.tasksOrActionsById[taskForProcessingResponse.processingTask.id] as Task);
  }

  updateWithOlderMessages(lazyloadResponse: LazyloadTasksOrActionsResponse) {
    this.addNewChunkOfOlderMessages(lazyloadResponse.tasksOrActionsById);
  }

  hasMoreTasksToLoad() {
    return this.notYetLoadedIds.length > 0;
  }

  nextTasksIds() {
    const n = Math.min(MESSAGE_PAGE_SIZE, this.notYetLoadedIds.length);
    return this.notYetLoadedIds.slice(-n);
  }

  // Returns the messages that need to have their day indicator updated after a lazyload
  mostRecentlyLoadedChunkPlusOneMessage(): AnyTaskOrActionType[] {
    const result = [];
    if (this.loadedChunks.length > 0) {
      result.push(...this.loadedChunks[0]);
    }
    if (this.loadedChunks.length > 1) {
      result.push(this.loadedChunks[1][0]);
    }
    return result;
  }

  // Returns the messages that were last lazyloaded
  mostRecentlyLoadedChunk(): AnyTaskOrActionType[] {
    const result = [];
    if (this.loadedChunks.length > 0) {
      result.push(...this.loadedChunks[0]);
    }
    return result;
  }

  isLoaded(taskOrActionId) {
    let result = false;
    for (const chunk of this.loadedChunks) {
      for (const taskOrAction of chunk) {
        if (taskOrAction?.id === taskOrActionId) {
          result = true;
        }
      }
    }
    return result;
  }

  // Returns the chunk that was created on page load; that is, the one with the most recent messages
  chunkWithMostRecentMessages() {
    return this.loadedChunks[this.loadedChunks.length - 1];
  }

  mostRecentTaskOrAction() {
    const mostRecentChunk = this.chunkWithMostRecentMessages();
    return mostRecentChunk[mostRecentChunk.length - 1];
  }

  hasTaskMoreRecentThanProcessedTask(processedTaskOrActionId: string): boolean {
    let result = false;
    let hasSeenProcessedTask = false;
    for (const chunk of this.loadedChunks) {
      for (const taskOrAction of chunk) {
        if (taskOrAction.type === TaskOrActionType.Task && hasSeenProcessedTask) {
          result = true;
        }
        if (taskOrAction.id === processedTaskOrActionId) {
          hasSeenProcessedTask = true;
        }
      }
    }
    return result;
  }

  idsUpTo(taskOrActionId: string) {
    if (taskOrActionId === '12345:task') {
      // Dummy processed task
      return [];
    }
    const index = this.notYetLoadedIds.indexOf(taskOrActionId);
    if (index === -1) {
      console.error(`ChunksAndIds.idsUpTo() could not find id ${taskOrActionId}`);
      return [];
    } else {
      return this.notYetLoadedIds.slice(index);
    }
  }

  /**
   * A chunk needs to display a "new messages below" indicator
   * iff it is the most recent chunk that the user has seen
   * and there are other chunks after it
   */
  chunkNeedsNewMessagesSeparator(chunk) {
    return chunk === this.mostRecentChunkSeen && !this.hasUserSeenAllMessages();
  }

  /**
   * Tells whether the last chunk (the one with the most recent message(s)) has been seen by the user
   */
  hasUserSeenAllMessages() {
    return this.mostRecentChunkSeen === this.chunkWithMostRecentMessages() || this.allUnseenMessagesAreFromLoggedUser;
  }

  /**
   * Indicates that the "Newer messages below" yellow button should be displayed
   * if there are indeed newer messages
   */
  showNewMessagesBelowButon() {
    this.allUnseenMessagesAreFromLoggedUser = false;
  }

  /**
   * Called when the user clicks the "new messages below" button,
   * to clear the "new messages" indicator line
   */
  userHasSeenAllMessages() {
    this.mostRecentChunkSeen = this.chunkWithMostRecentMessages();
    this.allUnseenMessagesAreFromLoggedUser = true;
  }

  private addNewChunkOfOlderMessages(tasksOrActionsById: TaskOrActionSet) {
    const loadedMessagesNumber = Object.keys(tasksOrActionsById).length;
    const loadedIds = this.notYetLoadedIds.splice(-loadedMessagesNumber, loadedMessagesNumber);
    loadedIds.forEach(id => {
      if (!tasksOrActionsById[id]) {
        console.warn('ChunksAndIds - taskOrAction not found in tasksOrActionsById: ' + id);
      }
    });
    this.loadedChunks.unshift(loadedIds.map(id => tasksOrActionsById[id]));
  }

  insertNewTaskOrAction(newTaskOrAction: AnyTaskOrActionType) {
    const clonedTaskOrAction = { ...newTaskOrAction }; // avoid collision with other subscribers
    const previouslyLatestTaskOrAction = this.mostRecentTaskOrAction();
    this.taskProcessService.setUIProperties([clonedTaskOrAction], previouslyLatestTaskOrAction);
    this.loadedChunks.push([clonedTaskOrAction]);
  }

  shouldReloadPageWhenNewTaskArrives() {
    for (const chunk of this.loadedChunks) {
      for (const taskOrAction of chunk) {
        if (taskOrAction.type === TaskOrActionType.Task) {
          const taskStateCode = (taskOrAction as Task).taskStateCode;
          const isPending = taskStateCode === TaskState.Pending;
          const isAssignedToMe =
            (taskOrAction as Task).taskStateCode === TaskState.Assigned &&
            this.alcmeonContextService.isMe((taskOrAction as GenericAction).adminUser);
          if (isPending || isAssignedToMe) {
            // there is at least one task with state assigned-to-me
            // or pending so we should not replace current task
            return false;
          }
        }
      }
    }
    return true;
  }

  replaceTaskOrActionIfAlreadyLoaded(newTaskOrAction: AnyTaskOrActionType): void {
    if (this.isLoaded(newTaskOrAction.id)) {
      this.replaceTaskOrAction(newTaskOrAction);
    }
  }

  private replaceTaskOrAction(newTaskOrAction: AnyTaskOrActionType) {
    for (const chunk of this.loadedChunks) {
      for (const taskOrAction of chunk) {
        if (taskOrAction && taskOrAction.id === newTaskOrAction.id) {
          this.taskProcessService.setUIProperties([newTaskOrAction], null);
          newTaskOrAction.dayIndicatorFormat = taskOrAction.dayIndicatorFormat;
          const index = chunk.indexOf(taskOrAction);
          chunk[index] = newTaskOrAction;
        }
      }
    }
  }
}
